import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ModuleLayout from '../components/ModuleLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import QRCode from 'react-qr-code';
import {
  QrCode, Plus, Trash2, Loader2, Download, Printer, Table2, Users, Eye,
  Pencil, LayoutGrid, List, RefreshCw, CircleDot, Clock, ShoppingCart,
  CheckCircle2, XCircle, Save, Wifi
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  available: { color: '#10B981', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-600', icon: CheckCircle2 },
  occupied: { color: '#EF4444', bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-600', icon: CircleDot },
  reserved: { color: '#F59E0B', bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-600', icon: Clock },
};

function StatsCards({ stats, t }) {
  if (!stats) return null;
  const cards = [
    { label: t('tables.totalTables'), value: stats.total_tables, icon: Table2, color: 'from-blue-500 to-indigo-600' },
    { label: t('tables.availableCount'), value: stats.available, icon: CheckCircle2, color: 'from-emerald-500 to-green-600' },
    { label: t('tables.occupiedCount'), value: stats.occupied, icon: CircleDot, color: 'from-red-500 to-rose-600' },
    { label: t('tables.todayDinein'), value: stats.today_dinein_orders, icon: ShoppingCart, color: 'from-amber-500 to-orange-600' },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tables-stats">
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <Card key={i} className="border-0 shadow-sm overflow-hidden">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] text-muted-foreground">{c.label}</span>
                <div className={`p-1.5 rounded-lg bg-gradient-to-br ${c.color}`}>
                  <Icon className="w-3.5 h-3.5 text-white" />
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-bold">{c.value}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function FloorMap({ tables, onTableClick, t }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3" data-testid="floor-map">
      {tables.map(table => {
        const sc = STATUS_CONFIG[table.status] || STATUS_CONFIG.available;
        const StatusIcon = sc.icon;
        return (
          <button key={table.id} onClick={() => onTableClick(table)}
            data-testid={`floor-table-${table.table_number}`}
            className={`relative p-3 sm:p-4 rounded-2xl border-2 ${sc.border} ${sc.bg} transition-all duration-200 hover:scale-105 hover:shadow-lg text-center cursor-pointer`}>
            {/* Status dot */}
            <div className="absolute top-2 end-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ background: sc.color, boxShadow: `0 0 6px ${sc.color}60` }} />
            </div>
            {/* Table number */}
            <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto rounded-xl flex items-center justify-center mb-2"
              style={{ background: `${sc.color}15`, border: `2px solid ${sc.color}30` }}>
              <span className="text-base sm:text-lg font-bold" style={{ color: sc.color }}>{table.table_number}</span>
            </div>
            <p className="text-xs font-semibold text-foreground truncate">{table.label}</p>
            <p className="text-[10px] text-muted-foreground flex items-center justify-center gap-0.5 mt-0.5">
              <Users className="w-2.5 h-2.5" /> {table.seats}
            </p>
            {/* Active order indicator */}
            {table.active_order && (
              <div className="mt-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold"
                style={{ background: `${sc.color}20`, color: sc.color }}>
                {table.active_order.total_price} {t('common.sar')}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function TableList({ tables, onEdit, onPreview, onDelete, onStatusChange, t }) {
  return (
    <div className="space-y-2" data-testid="tables-list">
      {tables.map(table => {
        const sc = STATUS_CONFIG[table.status] || STATUS_CONFIG.available;
        const StatusIcon = sc.icon;
        return (
          <Card key={table.id} className={`border shadow-sm`} data-testid={`table-row-${table.table_number}`}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                {/* Table info */}
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${sc.color}15`, border: `2px solid ${sc.color}30` }}>
                    <span className="text-sm font-bold" style={{ color: sc.color }}>{table.table_number}</span>
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{table.label}</span>
                      <Badge className={`text-[10px] ${sc.bg} ${sc.text} border-0`}>
                        <StatusIcon className="w-2.5 h-2.5 me-0.5" /> {t(`tables.status_${table.status}`)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {table.seats} {t('tables.seatsLabel')}</span>
                      {table.active_order && (
                        <span className="flex items-center gap-1 text-red-500 font-medium">
                          <ShoppingCart className="w-3 h-3" /> {table.active_order.total_price} {t('common.sar')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0 self-end sm:self-center">
                  <Select value={table.status} onValueChange={(v) => onStatusChange(table.id, v)}>
                    <SelectTrigger className="h-7 w-24 text-[10px]" data-testid={`status-select-${table.table_number}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">{t('tables.status_available')}</SelectItem>
                      <SelectItem value="occupied">{t('tables.status_occupied')}</SelectItem>
                      <SelectItem value="reserved">{t('tables.status_reserved')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => onPreview(table)} data-testid={`preview-qr-${table.table_number}`}>
                    <QrCode className="w-3 h-3" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(table)} data-testid={`edit-table-${table.table_number}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => onDelete(table.id)} data-testid={`delete-table-${table.table_number}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export default function TablesPage() {
  const { authHeaders, activeBranch } = useAuth();
  const { t } = useTranslation();
  const [tables, setTables] = useState([]);
  const [stats, setStats] = useState(null);
  const [siteUrl, setSiteUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bulkCount, setBulkCount] = useState(5);
  const [bulkSeats, setBulkSeats] = useState(4);
  const [creating, setCreating] = useState(false);
  const [viewMode, setViewMode] = useState('floor');
  const [previewTable, setPreviewTable] = useState(null);
  const [printData, setPrintData] = useState(null);
  const [editTable, setEditTable] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editSeats, setEditSeats] = useState(4);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!activeBranch) return;
    setLoading(true);
    try {
      const [tablesR, statsR, siteR] = await Promise.all([
        axios.get(`${API}/api/tables?branch_id=${activeBranch.id}`, authHeaders()),
        axios.get(`${API}/api/tables/stats?branch_id=${activeBranch.id}`, authHeaders()),
        axios.get(`${API}/api/tables/site-url?branch_id=${activeBranch.id}`, authHeaders()),
      ]);
      setTables(tablesR.data);
      setStats(statsR.data);
      setSiteUrl(siteR.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [activeBranch, authHeaders]);

  useEffect(() => { if (activeBranch) fetchAll(); }, [activeBranch, fetchAll]);

  const handleBulkCreate = async () => {
    if (!activeBranch || bulkCount < 1) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API}/api/tables/bulk-create?branch_id=${activeBranch.id}&count=${bulkCount}&seats=${bulkSeats}`, {}, authHeaders());
      toast.success(t('tables.created', { count: res.data.created }));
      fetchAll();
    } catch { toast.error(t('tables.createFailed')); }
    finally { setCreating(false); }
  };

  const handleDelete = async (tableId) => {
    if (!window.confirm(t('common.confirm') + '?')) return;
    try {
      await axios.delete(`${API}/api/tables/${tableId}`, authHeaders());
      toast.success(t('tables.deleted'));
      fetchAll();
    } catch { toast.error(t('tables.deleteFailed')); }
  };

  const handleStatusChange = async (tableId, newStatus) => {
    try {
      await axios.put(`${API}/api/tables/${tableId}`, { status: newStatus, label: '', seats: 0 }, authHeaders());
      setTables(prev => prev.map(t => t.id === tableId ? { ...t, status: newStatus } : t));
      fetchAll();
    } catch { toast.error(t('common.failed')); }
  };

  const openEdit = (table) => {
    setEditTable(table);
    setEditLabel(table.label);
    setEditSeats(table.seats);
  };

  const saveEdit = async () => {
    if (!editTable) return;
    setSaving(true);
    try {
      await axios.put(`${API}/api/tables/${editTable.id}`, {
        label: editLabel, seats: editSeats, status: editTable.status
      }, authHeaders());
      toast.success(t('common.success'));
      setEditTable(null);
      fetchAll();
    } catch { toast.error(t('common.failed')); }
    finally { setSaving(false); }
  };

  const handlePrintAll = async () => {
    if (!activeBranch) return;
    try {
      const res = await axios.get(`${API}/api/tables/print-all?branch_id=${activeBranch.id}`, authHeaders());
      setPrintData(res.data);
    } catch { toast.error(t('tables.printFailed')); }
  };

  const getQrUrl = (tableNumber) => {
    if (siteUrl?.has_site) {
      return `${siteUrl.base_url}/site/${siteUrl.site_id}?table=${tableNumber}`;
    }
    return `${API}/site/no-site?table=${tableNumber}`;
  };

  const handleFloorTableClick = (table) => {
    setPreviewTable(table);
  };

  if (!activeBranch) {
    return (
      <ModuleLayout moduleKey="operations">
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <Table2 className="w-12 h-12 text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold">{t('tables.selectBranch')}</h2>
        </div>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout moduleKey="operations">
      <div className="space-y-5 max-w-5xl mx-auto" data-testid="tables-page">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-bold text-xl sm:text-2xl text-foreground flex items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <QrCode className="w-5 h-5 text-primary" />
              </div>
              {t('tables.title')}
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">{t('tables.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/dashboard/operations/live-tables" data-testid="live-view-btn">
              <Button variant="default" size="sm" className="h-8 rounded-full gap-1.5 bg-gradient-to-l from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg shadow-amber-500/20">
                <Wifi className="w-3.5 h-3.5" /> {t('tables.liveView')}
              </Button>
            </Link>
            <Button variant="outline" size="sm" className="h-8 rounded-full gap-1" onClick={fetchAll} data-testid="refresh-tables">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            {tables.length > 0 && (
              <Button variant="outline" size="sm" onClick={handlePrintAll} className="h-8 rounded-full gap-1" data-testid="print-all-btn">
                <Printer className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{t('tables.printAllBtn')}</span>
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <StatsCards stats={stats} t={t} />

        {/* Create Section */}
        <Card className="bg-card border border-border/50">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Plus className="w-4 h-4 text-primary" /> {t('tables.createTablesTitle')}
            </h3>
            <div className="flex items-end gap-3 flex-wrap">
              <div>
                <Label className="text-xs mb-1 block">{t('tables.tableCountLabel')}</Label>
                <Input type="number" min={1} max={50} value={bulkCount} onChange={e => setBulkCount(parseInt(e.target.value) || 1)}
                  className="w-20 sm:w-24 h-9" data-testid="bulk-count-input" />
              </div>
              <div>
                <Label className="text-xs mb-1 block">{t('tables.seatCountLabel')}</Label>
                <Input type="number" min={1} max={20} value={bulkSeats} onChange={e => setBulkSeats(parseInt(e.target.value) || 4)}
                  className="w-20 sm:w-24 h-9" data-testid="bulk-seats-input" />
              </div>
              <Button onClick={handleBulkCreate} disabled={creating} className="h-9 rounded-full gap-1.5" data-testid="bulk-create-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {t('tables.createBtnText', { count: bulkCount })}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tables View */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
        ) : tables.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Table2 className="w-16 h-16 mx-auto mb-4 opacity-20" />
            <p>{t('tables.noTablesYet')}</p>
            <p className="text-xs mt-1">{t('tables.createFirst')}</p>
          </div>
        ) : (
          <>
            {/* View Toggle + Legend */}
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-4">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <div key={key} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: cfg.color }} />
                    <span className="text-[11px] text-muted-foreground">{t(`tables.status_${key}`)}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center border rounded-lg overflow-hidden">
                <button onClick={() => setViewMode('floor')} data-testid="view-floor"
                  className={`px-3 py-1.5 text-xs flex items-center gap-1 transition-colors ${viewMode === 'floor' ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted/60'}`}>
                  <LayoutGrid className="w-3.5 h-3.5" /> {t('tables.floorView')}
                </button>
                <button onClick={() => setViewMode('list')} data-testid="view-list"
                  className={`px-3 py-1.5 text-xs flex items-center gap-1 transition-colors ${viewMode === 'list' ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:bg-muted/60'}`}>
                  <List className="w-3.5 h-3.5" /> {t('tables.listView')}
                </button>
              </div>
            </div>

            {viewMode === 'floor' ? (
              <FloorMap tables={tables} onTableClick={handleFloorTableClick} t={t} />
            ) : (
              <TableList tables={tables} onEdit={openEdit} onPreview={setPreviewTable} onDelete={handleDelete} onStatusChange={handleStatusChange} t={t} />
            )}
          </>
        )}
      </div>

      {/* QR Preview Dialog */}
      <Dialog open={!!previewTable} onOpenChange={() => setPreviewTable(null)}>
        <DialogContent className="max-w-[calc(100vw-32px)] sm:max-w-sm" data-testid="qr-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-center">{previewTable?.label}</DialogTitle>
          </DialogHeader>
          {previewTable && (
            <div className="text-center space-y-4">
              {/* Status changer */}
              <div className="flex items-center justify-center gap-2">
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  const isActive = previewTable.status === key;
                  return (
                    <button key={key} onClick={() => { handleStatusChange(previewTable.id, key); setPreviewTable({ ...previewTable, status: key }); }}
                      data-testid={`qr-status-${key}`}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${isActive ? `${cfg.bg} ${cfg.text} ${cfg.border}` : 'border-border text-muted-foreground hover:bg-muted/60'}`}>
                      <Icon className="w-3 h-3" /> {t(`tables.status_${key}`)}
                    </button>
                  );
                })}
              </div>
              {/* QR Code */}
              <div className="bg-white p-4 rounded-xl inline-block mx-auto" data-testid="table-qr-preview">
                <QRCode value={getQrUrl(previewTable.table_number)} size={200} />
              </div>
              <p className="text-[11px] text-muted-foreground">{t('tables.qrDescription', { num: previewTable.table_number })}</p>
              {/* Active order info */}
              {previewTable.active_order && (
                <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/20">
                  <p className="text-xs font-semibold text-red-600 flex items-center justify-center gap-1">
                    <ShoppingCart className="w-3.5 h-3.5" /> {t('tables.activeOrder')}: {previewTable.active_order.total_price} {t('common.sar')}
                  </p>
                </div>
              )}
              {/* Actions */}
              <div className="flex gap-2">
                <Button className="flex-1 rounded-full gap-1.5" data-testid="download-table-qr"
                  onClick={() => {
                    const svg = document.querySelector('[data-testid="table-qr-preview"] svg');
                    if (!svg) return;
                    const canvas = document.createElement('canvas');
                    canvas.width = 600; canvas.height = 700;
                    const ctx = canvas.getContext('2d');
                    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, 600, 700);
                    ctx.fillStyle = '#000'; ctx.font = 'bold 28px Tajawal, sans-serif'; ctx.textAlign = 'center';
                    ctx.fillText(previewTable.label, 300, 50);
                    ctx.font = '18px Tajawal, sans-serif'; ctx.fillStyle = '#666';
                    ctx.fillText(t('tables.scanToOrder'), 300, 680);
                    const svgData = new XMLSerializer().serializeToString(svg);
                    const img = new Image();
                    img.onload = () => { ctx.drawImage(img, 100, 80, 400, 400); const a = document.createElement('a'); a.download = `table-${previewTable.table_number}-qr.png`; a.href = canvas.toDataURL('image/png'); a.click(); };
                    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                  }}>
                  <Download className="w-4 h-4" /> {t('tables.downloadQR')}
                </Button>
                <Button variant="outline" className="rounded-full gap-1.5" onClick={() => { openEdit(previewTable); setPreviewTable(null); }}>
                  <Pencil className="w-4 h-4" /> {t('common.edit')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Table Dialog */}
      <Dialog open={!!editTable} onOpenChange={() => setEditTable(null)}>
        <DialogContent className="max-w-[calc(100vw-32px)] sm:max-w-sm" data-testid="edit-table-dialog">
          <DialogHeader>
            <DialogTitle>{t('tables.editTable')}</DialogTitle>
          </DialogHeader>
          {editTable && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs mb-1.5 block">{t('tables.labelField')}</Label>
                <Input value={editLabel} onChange={e => setEditLabel(e.target.value)} className="h-9" data-testid="edit-label" />
              </div>
              <div>
                <Label className="text-xs mb-1.5 block">{t('tables.seatCountLabel')}</Label>
                <Input type="number" min={1} max={20} value={editSeats} onChange={e => setEditSeats(parseInt(e.target.value) || 4)}
                  className="h-9 w-24" data-testid="edit-seats" />
              </div>
              <Button onClick={saveEdit} disabled={saving} className="w-full rounded-full gap-1.5" data-testid="save-edit-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('common.save')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Print All Dialog */}
      <Dialog open={!!printData} onOpenChange={() => setPrintData(null)}>
        <DialogContent className="max-w-[calc(100vw-32px)] sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('tables.printTitle', { name: printData?.branch_name })}</DialogTitle>
          </DialogHeader>
          {printData && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 p-2 sm:p-4" id="print-area" data-testid="print-all-grid">
                {printData.tables.map(tbl => (
                  <div key={tbl.table_id} className="text-center border border-border rounded-xl p-3 sm:p-4">
                    <p className="font-bold text-base sm:text-lg mb-2">{tbl.label}</p>
                    <div className="bg-white p-2 sm:p-3 rounded-lg inline-block mx-auto">
                      {tbl.url && <QRCode value={tbl.url} size={120} />}
                    </div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground mt-2">{t('tables.scanToOrder')}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 p-4 border-t">
                <Button className="flex-1 rounded-full gap-1.5" onClick={() => window.print()} data-testid="print-qrs-btn">
                  <Printer className="w-4 h-4" /> {t('tables.printBtn')}
                </Button>
                <Button variant="outline" className="rounded-full" onClick={() => setPrintData(null)}>{t('tables.closeBtn')}</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ModuleLayout>
  );
}
